/**
 * El lector de códigos en todas las pantallas (2026-10-03). Va una sola vez,
 * en `AppLayout`. Ver `lib/lectorGlobal.ts`.
 *
 * Reconoce SOLO los dos modos en que un lector entrega el código, sin que nadie
 * tenga que configurar nada:
 *
 *   · «Teclado» (el USB, el Bluetooth, y el del teléfono con esa salida): se
 *     escucha el teclado de la pantalla entera (`useEscucharElLector`).
 *   · «Escribir en la casilla» / «direct fill» (el incluido en algunos
 *     teléfonos): escribe en la casilla que tiene el cursor, y solo si acepta
 *     texto. Para ese está `CasillaDelLector`, una casilla invisible que toma
 *     el cursor al apretar el gatillo (ver `useGatilloDelLector`).
 *
 * Los dos terminan en `entregar`: le llega a la pantalla que pidió los
 * códigos, o sale la consulta rápida.
 */

import { useEffect, useRef, useState } from "react";
import {
  ENTRE_TECLAS_MS,
  LARGO_MINIMO,
  LARGO_SIN_ENTER,
  PAUSA_SIN_ENTER_MS,
  aceptaTexto,
  cerrarParaElLector,
  entregar,
  useEscucharElLector,
  useGatilloDelLector,
} from "@/lib/lectorGlobal";
import { useEscucharAlTelefono } from "@/lib/telefonoDeLaPc";
import { ConsultaRapida } from "./ConsultaRapida";

export function LectorGlobal() {
  // `vez`: leer el mismo código dos veces vuelve a consultarlo.
  const [consulta, setConsulta] = useState<{ codigo: string; vez: number } | null>(null);
  const alLeer = (codigo: string) => entregar(codigo, (c) => setConsulta({ codigo: c, vez: Date.now() }));
  useEscucharElLector(alLeer);
  // El teléfono unido a esta PC por QR llega por el mismo camino que un lector
  // (2026-11-02). Ver `lib/telefonoDeLaPc.ts`.
  useEscucharAlTelefono(alLeer);
  return (
    <>
      <CasillaDelLector alLeer={alLeer} />
      {consulta && (
        <ConsultaRapida
          key={consulta.vez}
          codigo={consulta.codigo}
          onOtro={(c) => setConsulta({ codigo: c, vez: Date.now() })}
          onCerrar={() => setConsulta(null)}
        />
      )}
    </>
  );
}

/**
 * La casilla invisible del lector en modo «escribir en la casilla».
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  CÓMO RECIBE EL CURSOR: CON CADA TOQUE DEL DEDO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ese tipo de lector escribe a través del teclado del teléfono, y el navegador
 * solo lo conecta a una casilla que recibió el cursor con un toque de la
 * persona (lo enseñó el video del dueño con el Consultor; ver
 * `CapturaDelLector`). Así que cada toque en la pantalla que no sea en otra
 * casilla le da el cursor a esta, en ese mismo instante, sin abrir el teclado
 * (`inputMode="none"`).
 *
 *   · Solo con el DEDO (o un lápiz): con el ratón no se le quita el cursor a
 *     nadie. En la computadora el lector teclea, y eso ya se escucha.
 *   · Si otra casilla tiene el cursor —se está escribiendo un nombre, o una
 *     lista de sugerencias se lo guardó a propósito— no se toca.
 *   · Al abrir la app hace falta un primer toque cualquiera; desde ahí queda
 *     lista mientras no se toque otra casilla.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ LO QUE SE ESCRIBE AQUÍ A MANO NO ES UN CÓDIGO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * En un portátil con pantalla táctil, después de tocar la pantalla el teclado
 * de verdad escribiría aquí. Se distingue igual que en la escucha del teclado:
 * el lector escribe el código de una vez, o letra por letra en pocos
 * milisegundos; una persona tarda más. Lo lento se descarta.
 */
function CasillaDelLector({ alLeer }: { alLeer: (codigo: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const alLeerRef = useRef(alLeer);
  alLeerRef.current = alLeer;
  const espera = useRef<number | undefined>(undefined);
  // Cómo fue llegando lo escrito: para saber si fue el lector o una persona.
  const llegada = useRef({ inicio: 0, ultima: 0, veces: 0, largo: 0, deGolpe: false });

  // ⚠️ El que de verdad hace funcionar al lector «direct fill» (2026-10-03): al
  // apretar el gatillo, esta casilla acepta texto por un instante. Con el
  // toque de abajo solo no bastaba: ese lector no escribe en `inputMode="none"`.
  useGatilloDelLector(ref);

  useEffect(() => {
    let conElDedo = false;
    const alApoyar = (e: PointerEvent) => {
      conElDedo = e.pointerType === "touch" || e.pointerType === "pen";
    };
    // En `click` y no en `pointerdown`: el `mousedown` que viene después
    // movería el cursor al sitio tocado y se lo quitaría a la casilla.
    const alTocar = (e: MouseEvent) => {
      const casilla = ref.current;
      if (!conElDedo || !casilla) return;
      if (aceptaTexto(e.target)) return;
      const activa = document.activeElement;
      if (activa !== casilla && aceptaTexto(activa)) return;
      casilla.focus({ preventScroll: true });
    };
    document.addEventListener("pointerdown", alApoyar, true);
    document.addEventListener("click", alTocar, true);
    return () => {
      document.removeEventListener("pointerdown", alApoyar, true);
      document.removeEventListener("click", alTocar, true);
      window.clearTimeout(espera.current);
    };
  }, []);

  function leer(conEnter: boolean) {
    window.clearTimeout(espera.current);
    const casilla = ref.current;
    if (!casilla) return;
    const codigo = casilla.value.replace(/[\r\n\t]/g, "").trim();
    casilla.value = "";
    // Vuelve a esperar sin teclado: si el gatillo la abrió, se cierra.
    if (casilla.getAttribute("inputmode") !== "none") {
      cerrarParaElLector(casilla);
      casilla.blur();
    }
    const { inicio, ultima, veces, deGolpe } = llegada.current;
    llegada.current = { inicio: 0, ultima: 0, veces: 0, largo: 0, deGolpe: false };

    const rapido = deGolpe || veces <= 1 || (ultima - inicio) / (veces - 1) <= ENTRE_TECLAS_MS;
    if (!rapido) return;
    // Sin Enter, un código corto se confundiría con cualquier cosa; escrito de
    // golpe, en cambio, solo pudo ser el lector.
    const minimo = conEnter || deGolpe ? LARGO_MINIMO : LARGO_SIN_ENTER;
    if (codigo.length >= minimo) alLeerRef.current(codigo);
  }

  return (
    <input
      ref={ref}
      aria-hidden="true"
      tabIndex={-1}
      inputMode="none"
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      data-casilla-del-lector=""
      // Fuera de la vista pero con tamaño: una casilla de 0 px o `display:none`
      // no recibe el cursor en algunos teléfonos.
      className="pointer-events-none fixed left-0 top-0 h-px w-px opacity-0"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          e.stopPropagation();
          leer(true);
        }
      }}
      onInput={(e) => {
        const valor = e.currentTarget.value;
        const ahora = performance.now();
        const l = llegada.current;
        if (l.veces === 0) l.inicio = ahora;
        l.veces += 1;
        l.ultima = ahora;
        if (valor.length - l.largo >= 2) l.deGolpe = true;
        l.largo = valor.length;
        // Algunos lectores escriben el Enter como un salto de línea.
        if (/[\r\n]/.test(valor)) {
          leer(true);
          return;
        }
        window.clearTimeout(espera.current);
        espera.current = window.setTimeout(() => leer(false), PAUSA_SIN_ENTER_MS);
      }}
    />
  );
}
